import React, { useState, useEffect } from "react";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import {
  FileErrors,
  FileValidators,
  useFileHandler,
} from "hooks/useFileHandler";
import { NewButton, NewButtonSize, NewTextField } from "@bale/ui";
import Footer from "components/commons/footer/Footer";
import ImageInput from "components/commons/ImageInput/ImageInput";
import { strings } from "intl/fa";
import { BaleJoinLink, WebUrl } from "utils/regex";
import commonStyles from "../Common.module.scss";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import {
  disableButton,
  formatAdUrl,
  formatStrings,
  getFileFromBlob,
} from "utils/utils";
import { useAppDispatch, useAppSelector } from "utils/store";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import { toast } from "react-toastify";
import { useRequest } from "hooks/useRequest";
import { GetMemberCountThunk } from "core/membersCount/membersCount.thunk";
import {
  validateForbiddenPlatforms,
  validateOnlyEnglish,
  LinkErrors,
  TitleAndTextErrors,
  validateBaleUrl,
} from "./validateInfo";
import { digitsToHindi } from "@bale/utils";
import PriceChangeModal from "components/commons/modal/priceChangeModal/PriceChangeModal";
import { MAX_DIALOG_MESSAGE_IMAGE_SIZE } from "utils/constants";
import { useDeepLinks } from "hooks/useDeepLinks";

const config = {
  titleMaxLength: 25,
  textMaxLength: 35,
  fileMaxSize: MAX_DIALOG_MESSAGE_IMAGE_SIZE,
};
type Props = {
  isCampaign?: boolean;
};
const InfoStep: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [showForceJoinWarning, setShowForceJoinWarning] = useState(false);
  const { titleMaxLength, textMaxLength, fileMaxSize } = config;
  const { title, link, description, imageBlob } = useAppSelector(
    (state) => state.orderAd.info
  );
  const { click_price, join_click_price } = useAppSelector(
    (state) => state.configs.configs
  );
  const { isDeepLink } = useDeepLinks();

  const { sendRequest: getMembers, loading } = useRequest(GetMemberCountThunk);

  const adTitleHandler = useInputHandler({
    initialValue: title,
    rules: [
      GeneralValidators.required,
      validateOnlyEnglish,
      GeneralValidators.maxLength(titleMaxLength),
    ],
    errorMessages: {
      [GeneralErrors.MaximumLength]: strings.order.sponsored.info.long_title,
      [GeneralErrors.Required]: strings.common.error.required,
      [TitleAndTextErrors.OnlyEnglish]:
        strings.dialog_ad_form.info_step.errors.only_english,
    },
    getDigitsHindi: true,
    beforeBlur: (value) => {
      return validateSameTitleAndText(value, true);
    },
  });
  const adTextHandler = useInputHandler({
    initialValue: description,
    rules: [
      GeneralValidators.required,
      validateOnlyEnglish,
      GeneralValidators.maxLength(textMaxLength),
    ],
    errorMessages: {
      [GeneralErrors.MaximumLength]: strings.order.sponsored.info.long_title,
      [GeneralErrors.Required]: strings.common.error.required,
      [TitleAndTextErrors.OnlyEnglish]:
        strings.dialog_ad_form.info_step.errors.only_english,
    },
    getDigitsHindi: true,
    beforeBlur: (value) => {
      return validateSameTitleAndText(value, false);
    },
  });

  const adLinkRules = [
    GeneralValidators.required,
    GeneralValidators.regexMatch(WebUrl),
    validateBaleUrl,
    validateForbiddenPlatforms,
  ];
  const adLinkHandler = useInputHandler({
    initialValue: link,
    rules: props.isCampaign
      ? [...adLinkRules, GeneralValidators.excludeWithRegex(BaleJoinLink)]
      : adLinkRules,
    showDigitsLatin: true,
    errorMessages: {
      [GeneralErrors.RegexMatch]:
        strings.dialog_ad_form.info_step.invalid_link_error,
      [GeneralErrors.ExcludeWithRegex]:
        strings.dialog_ad_form.info_step.join_link_error,
      [GeneralErrors.Required]: strings.common.error.required,
      [LinkErrors.ForbiddenPlatform]:
        strings.dialog_ad_form.info_step.errors.forbiddenPlatforms,
      [LinkErrors.WrongBaleUrl]:
        strings.dialog_ad_form.info_step.invalid_link_error,
    },
  });

  const adImageHandler = useFileHandler({
    rules: [FileValidators.maximumSize(fileMaxSize), FileValidators.required],
    errorMessages: {
      [FileErrors.MaximumSize]: strings.common.error.image_max_size,
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });

  useEffect(() => {
    if (adTextHandler.error === TitleAndTextErrors.SameTitleAndText) {
      adTextHandler.setError(undefined);
    }
    if (adTitleHandler.error === TitleAndTextErrors.SameTitleAndText) {
      adTitleHandler.setError(undefined);
    }
  }, [adTitleHandler.rawValue, adTextHandler.rawValue]);

  const validateSameTitleAndText = (
    currentValue: string,
    isValidatingTitle: boolean
  ): boolean => {
    if (
      !adTextHandler.rawValue ||
      !adTitleHandler.rawValue ||
      adTextHandler.error === TitleAndTextErrors.SameTitleAndText ||
      adTitleHandler.error === TitleAndTextErrors.SameTitleAndText
    ) {
      return false;
    }
    if (isValidatingTitle) {
      if (currentValue === adTextHandler.rawValue) {
        adTitleHandler.setError(TitleAndTextErrors.SameTitleAndText);
        return true;
      }
    } else {
      if (adTitleHandler.rawValue === currentValue) {
        adTextHandler.setError(TitleAndTextErrors.SameTitleAndText);
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (imageBlob) {
      getFileFromBlob(imageBlob, "image.png").then((imageFile) => {
        adImageHandler.setValue(imageFile);
      });
    }
  }, []);

  const dispatch = useAppDispatch();

  const isFormInvalid = disableButton(
    adTitleHandler,
    adTextHandler,
    adLinkHandler,
    adImageHandler
  );

  const saveData = (onSuccess: (status: boolean) => void) => {
    if (isFormInvalid) {
      toast.info(strings.common.error.all_required);
      return false;
    }
    getMembers({
      link: formatAdUrl(adLinkHandler.rawValue),
      onSuccess(membersRes: any) {
        if (membersRes.count > 0 && membersRes.count < 200) {
          toast.info(strings.dialog_ad_form.info_step.errors.low_members, {
            toastId: "low_members",
          });
          onSuccess(false);
          return;
        }
        if (
          membersRes.count === -1 &&
          adLinkHandler.rawValue.includes("ble.ir") &&
          !isDeepLink(adLinkHandler.rawValue)
        ) {
          toast.info(strings.dialog_ad_form.info_step.errors.no_channel_found, {
            toastId: "not_valid_channel",
          });
          onSuccess(false);
          return;
        }
        dispatch(
          OrderAdActions.setOrderAdInfo({
            title: adTitleHandler.rawValue,
            description: adTextHandler.rawValue,
            link: adLinkHandler.rawValue,
            imageBlob: URL.createObjectURL(adImageHandler.file as Blob),
          })
        );
        onSuccess(true);
        return;
      },
    });
  };
  const handleSubmit = () => {
    if (BaleJoinLink.test(adLinkHandler.rawValue)) {
      setShowForceJoinWarning(true);
      return;
    }
    dispatch(OrderAdActions.setLinkIsForceJoin(false));
    navigateToNextPhase();
  };

  const forceJoinConfirmationHandler = async () => {
    dispatch(OrderAdActions.setLinkIsForceJoin(true));
    await navigateToNextPhase();
  };

  const navigateToNextPhase = async () => {
    const nextPage = props.isCampaign
      ? OrderAdPages.CampaignMoreInfo
      : OrderAdPages.DialogMoreInfo;
    saveData((status) => status && dispatch(OrderAdActions.setPage(nextPage)));
  };

  return (
    <>
      <CardWrapper className={commonStyles.Card}>
        <div className={commonStyles.FormItem}>
          <NewTextField
            {...adTitleHandler}
            className={commonStyles.TextField}
            direction="rtl"
            multiline={false}
            data-testid="ad-title-input"
            maxLength={titleMaxLength}
            showMaxLength
            label={strings.dialog_ad_form.info_step.title_label}
            placeholder={
              strings.dialog_ad_form.info_step.title_help_placeholder
            }
          />
        </div>
        <div className={commonStyles.FormItem}>
          <NewTextField
            {...adTextHandler}
            className={commonStyles.TextField}
            direction="rtl"
            data-testid="ad-text-input"
            multiline={false}
            maxLength={textMaxLength}
            showMaxLength
            label={strings.dialog_ad_form.info_step.text_label}
            placeholder={strings.dialog_ad_form.info_step.text_placeholder}
          />
        </div>
        <div className={commonStyles.FormItem}>
          <ImageInput
            {...adImageHandler}
            helpText={strings.dialog_ad_form.info_step.image_size_limit_info}
            beforeSelectLabel={strings.common.input.image.label}
          />
        </div>
        <div className={commonStyles.FormItem}>
          <NewTextField
            {...adLinkHandler}
            direction={adLinkHandler.value ? "ltr" : "rtl"}
            className={commonStyles.TextField}
            label={strings.dialog_ad_form.info_step.link_label}
            data-testid="ad-link-input"
            placeholder={strings.dialog_ad_form.info_step.link_placeholder}
            helpText={
              adLinkHandler.error
                ? undefined
                : strings.dialog_ad_form.info_step.link_help
            }
          />
        </div>
      </CardWrapper>
      <Footer>
        <NewButton
          onClick={handleSubmit}
          style={{ width: "100%", maxWidth: 336, margin: "0 16px" }}
          size={NewButtonSize.LARGE}
          data-testid="submit-button"
          disabledJustColor={isFormInvalid}
          loading={loading}
        >
          {/* why wrapped by span? look at https://github.com/facebook/react/issues/11538#issuecomment-390386520 */}
          <span>{strings.dialog_ad_form.info_step.submit}</span>
        </NewButton>
      </Footer>
      {showForceJoinWarning && (
        <PriceChangeModal
          onPrimaryClicked={forceJoinConfirmationHandler}
          secondaryButton={{
            onSecondaryClicked: () => setShowForceJoinWarning(false),
          }}
          changeInfo={{
            afterPrice: click_price / 10,
            beforePrice: join_click_price / 10,
          }}
          title={strings.dialog_ad_form.info_step.force_join}
          description={digitsToHindi(
            formatStrings(
              strings.dialog_ad_form.info_step.force_join_modal_text,
              {
                newPrice: join_click_price / 10,
              }
            )
          )}
          labelText={strings.dialog_ad_form.info_step.price_per_click}
        />
      )}
    </>
  );
};

export default InfoStep;
